import axios from 'axios';
import { mutate } from 'swr';
import { stringify } from 'qs';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { withQueryParams } from '@shared/commonFunctions';
import { getError } from '@shared/utils/helpers';
import { OverrideFormFormikStateType } from './components/OverrideModal/types';
import { IDialerData, ILead, INotesRequestAddBody } from './types/interfaces/interfaces';

interface ISendOneClickResponse {
  message: string;
  success: boolean;
  warning: boolean;
}

const path = `/${SERVICES.LEAD}/leads`;
const leadPath = `/${SERVICES.LEAD}/lead`;

export const requestCreateLead = (data: ILead) => {
  return mutate(path, async() => {
    try {
      await axios.post(path, data);
      Notistack.successMessage(' Lead - Created Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestUpdateLead = (data: ILead) => {
  return mutate(path, async() => {
    try {
      await axios.put(path, data);
      Notistack.successMessage('Lead - Updated Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDeleteLead = (data: ILead) => {
  return mutate(path, async() => {
    try {
      await axios.delete(`${path}/${data.id}`);
      Notistack.successMessage('Lead - Deleted Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestCreateNewDeal = (
  leadData: ILead | null,
  popupData: OverrideFormFormikStateType,
  contactId: string,
) => {
  const path = `/${SERVICES.DEAL}/contacts`;
  return mutate(path, async() => {
    try {
      const resp = await axios.post(`${path}/${contactId}/leads/${leadData?.id || ''}/deals`, {
        lenderCode: popupData.lenderCode,
        rate: popupData.rate,
        term: popupData.term,
        loanAmount: leadData?.assetLienAmount,
        type: 1,
        dealUsers: null,
        isPreApproval: false,
        assetId: leadData?.assetId,
      });
      Notistack.successMessage('New Deal created successfully');
      return resp;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestAddCommunication = (data: INotesRequestAddBody) => {
  return mutate(leadPath, async() => {
    try {
      await axios.post(`${leadPath}/${data.leadId}/notes`, data);
      Notistack.successMessage('Lead - Add Communication Successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestSendOneClick = (contactId: string, leadId = '') => {
  return mutate(`/${SERVICES.LEAD}/contacts/`, async() => {
    try {
      const response = await axios.post<ISendOneClickResponse>(
        withQueryParams(
          `/${SERVICES.LEAD}/contacts/${contactId}/oneclick/send`,
          {
            leadId: leadId,
          },
          false,
        ),
      );

      if (response.data.success) {
        Notistack.successMessage(response.data.message);
      }

      if (response.data.warning) {
        Notistack.infoMessage(response.data.message);
      }

      if (!response.data.success && !response.data.warning) {
        Notistack.generalError(response.data.message);
      }
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestDialerCallData = async (phoneNumbers: string[]) => {
  try {
    if (!phoneNumbers || phoneNumbers.length === 0) {
      return [];
    }

    const response = await axios.get<IDialerData[]>(
      `/${SERVICES.DIALER}/api/Dialer/LiveVoxCallDataByPhoneDialed`,
      {
        params: {
          phoneNumberArr: phoneNumbers,
        },
        paramsSerializer: params => {
          return stringify(params);
        }   
      }
    );
    return response.data;
  }
  catch (err) {
    Notistack.generalError(getError(err));
  }
};

